import React, { useEffect, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { waitForAngular } from 'App/AngularApp';
import Page from 'App/pages/Page/Page';

type PortedRouteConfig = {
	angularState: string;
	featureFlag: string;
};

const portedRoutes = [
	{
		angularState: 'findProspects',
		featureFlag: 'PORTED_ROUTE_FIND_PROSPECTS'
	} as const,
	{
		angularState: 'appointments',
		featureFlag: 'REACT_APPOINTMENTS_LIST'
	} as const
] satisfies PortedRouteConfig[];

const getFeatureFlag = (angularState: string) => {
	const portedRoute = portedRoutes.find(route => route.angularState === angularState);
	return portedRoute?.featureFlag || '';
};

export const setupPortedAngularStates = () => {
	for (const route of portedRoutes) {
		if (Tools.FeatureHelper.hasSoftDeployAccess(route.featureFlag)) {
			// Haxxing to make angular state not do anything
			// Resolves cannot be disabled like this - need to be handled separately
			const oldState = Tools.$state.get(route.angularState) as any;

			delete oldState.controller;
			delete oldState.controllerProvider;
			delete oldState.templateProvider;
			delete oldState.templateUrl;

			oldState.template = '<div></div>';
		}
	}
};

export const checkPortedResolve = async <TReturn extends any>(angularState: string, resolveFn: () => TReturn) => {
	const flag = getFeatureFlag(angularState);
	await waitForAngular();
	return Tools.AppService.loadedPromise.then(() => {
		if (Tools.FeatureHelper.hasSoftDeployAccess(flag)) {
			return;
		}
		return resolveFn();
	});
};

export const reactRouteCompatibility = (
	Component: React.ComponentType<any>,
	{
		featureFlag,
		// probably needs to be more advanced idk it'll do for now
		wrapInPage = true,
		pageProps = {},
		onMount,
		getKey
	}: {
		featureFlag: string;
		wrapInPage?: boolean;
		pageProps?: { [key: string]: any };
		/**
		 * Use to include side effects or data from resolve functions
		 * @return {object} Object with props to pass to the component
		 */
		onMount?: ({ params, query }: { params: any; query: URLSearchParams }) => object | void;
		getKey?: ({ params, query }: { params: any; query: URLSearchParams }) => string;
	}
) => {
	return (props: any) => {
		const hasFeatureFlag = Tools.FeatureHelper.hasSoftDeployAccess(featureFlag);
		const [resolvedProps, setResolvedProps] = useState<any>(null);
		const [key, setKey] = useState<string | undefined>(undefined);

		const { params } = useRouteMatch();
		const { search } = useLocation();

		const query = new URLSearchParams(search);

		useEffect(() => {
			if (hasFeatureFlag && onMount) {
				const resolved = onMount({ params, query });
				setResolvedProps(resolved ?? {});
			}
			if (getKey) {
				setKey(getKey({ params, query }));
			}
		}, [params, search]);

		// No feature flag, no render.
		if (!hasFeatureFlag) {
			return null;
		}

		if (onMount && !resolvedProps) {
			return null;
		}

		return wrapInPage ? (
			<Page {...pageProps}>
				<Component key={key} {...props} {...resolvedProps} />
			</Page>
		) : (
			<Component key={key} {...props} {...resolvedProps} />
		);
	};
};
